<template>
  <v-dialog v-model="internalDialog" max-width="1200px" persistent scrollable :style="dialogStyle">
    <v-card class="unified-modal">
      <!-- Header com Tabs -->
      <v-tabs
        v-model="activeTab"
        centered
        grow
        slider-color="primary"
        class="unified-tabs"
      >
        <v-tab class="tab-button">
          <v-icon left>mdi-update</v-icon>
          Atualizações
        </v-tab>
        <v-tab class="tab-button">
          <v-icon left>mdi-cog</v-icon>
          Configuração
        </v-tab>
        <v-tab class="tab-button">
          <v-icon left>mdi-file-document-multiple</v-icon>
          Logs
        </v-tab>
      </v-tabs>

      <!-- Tab Content -->
      <v-tabs-items v-model="activeTab" class="tab-items-container">
        <!-- ABA 1: ATUALIZAÇÕES -->
        <v-tab-item class="tab-content-area">
          <v-card-text class="pa-2" :style="cardTextStyle">
            <div v-if="selectedHost">
              <!-- Linha para cada sistema -->
              <div
                v-for="sistema in sistemasDisponiveis"
                :key="sistema"
                v-show="hasSystem(sistema)"
                class="system-row"
              >
                <!-- Coluna 1: Info do Sistema e Botão Manual -->
                <div class="col-info">
                  <div class="sys-header position-relative">
                    <!-- Score Indicator (Canto superior direito) -->
                    <!-- Não exibir se sistema está atualizado (score 0) -->
                    <div
                      v-if="deveMostrarScore(scoresSistemas[sistema])"
                      class="score-indicator"
                      :title="getScoreTooltip(scoresSistemas[sistema], sistema)"
                    >
                      <div v-if="deveMostrarIcone(scoresSistemas[sistema])" class="score-icon-only">
                        <span class="score-icon">{{ getScoreIcon(scoresSistemas[sistema]) }}</span>
                      </div>
                      <div v-else class="score-color-circle" :style="getScoreColorStyle(scoresSistemas[sistema])"></div>
                    </div>
                    
                    <div class="sys-icon">
                      <img
                        :src="getIconeSistema(sistema)"
                        :alt="sistema"
                        class="sistema-icon-small"
                      />
                    </div>
                    <div>
                      <div class="sys-name">{{ sistema }}</div>
                      <div
                        class="sys-version"
                        :class="{ 'ok': isAtualizado(sistema) }"
                      >
                        <v-icon small :color="isAtualizado(sistema) ? 'success' : 'error'">
                          {{ isAtualizado(sistema) ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                        {{ getSystemVersion(sistema) || 'N/A' }}
                        {{ isAtualizado(sistema) ? '' : '(Desatualizado)' }}
                      </div>
                    </div>
                  </div>
                  <v-tooltip bottom max-width="320">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-btn
                          class="btn-manual"
                          small
                          block
                          :color="getButtonColor(sistema)"
                          :disabled="isButtonDisabled(sistema)"
                          :loading="atualizando === sistema"
                          @click="handleButtonClick(sistema)"
                        >
                          <v-icon left small>{{ getButtonIcon(sistema) }}</v-icon>
                          {{ getButtonText(sistema) }}
                        </v-btn>
                      </span>
                    </template>
                    <span v-if="sistema === 'VSUP' && !podeAtualizarVSUP && mensagemBloqueioVSUP">{{ mensagemBloqueioVSUP }}</span>
                    <span v-else>{{ getScoreTooltip(scoresSistemas[sistema], sistema) || 'Atualizar sistema ' + sistema }}</span>
                  </v-tooltip>
                </div>

                <!-- Coluna 2: Cronograma Diário -->
                <div class="col-config">
                  <label class="section-label">Cronograma Diário</label>
                  <div class="input-group">
                    <v-tooltip v-if="sistema === 'VSUP' && !podeAtualizarVSUP" bottom max-width="320">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" style="display: inline-block;">
                          <v-switch
                            v-model="cronogramaForm[sistema.toLowerCase()].enabled"
                            dense
                            hide-details
                            class="mt-0"
                            :disabled="loadingCronograma || (sistema === 'VSUP' && !podeAtualizarVSUP)"
                          ></v-switch>
                        </span>
                      </template>
                      <span>{{ mensagemBloqueioVSUP || 'Atualize o VSUC para 8.04a ou superior para habilitar o cronograma do VSUP.' }}</span>
                    </v-tooltip>
                    <v-switch
                      v-else
                      v-model="cronogramaForm[sistema.toLowerCase()].enabled"
                      dense
                      hide-details
                      class="mt-0"
                      :disabled="loadingCronograma"
                    ></v-switch>
                    <v-text-field
                      v-model="cronogramaForm[sistema.toLowerCase()].time"
                      type="time"
                      dense
                      hide-details
                      outlined
                      class="time-input"
                      :disabled="!cronogramaForm[sistema.toLowerCase()].enabled || loadingCronograma || (sistema === 'VSUP' && !podeAtualizarVSUP)"
                    ></v-text-field>
                    <span class="status-text">
                      {{ cronogramaForm[sistema.toLowerCase()].enabled ? 'Ativo' : 'Inativo' }}
                    </span>
                  </div>
                </div>

                <!-- Coluna 3: Agendamento Único (apenas horário - executa hoje) -->
                <div class="col-config">
                  <label class="section-label">Agendamento Único (Hoje)</label>
                  
                  <!-- VSUP bloqueado: VSUC deve estar em 8.04a ou superior -->
                  <div v-if="sistema === 'VSUP' && !podeAtualizarVSUP" class="agendamento-bloqueado">
                    <v-chip
                      small
                      color="warning"
                      text-color="white"
                      class="bloqueio-chip"
                    >
                      <v-icon left small>mdi-lock</v-icon>
                      VSUP bloqueado
                    </v-chip>
                    <span class="bloqueio-texto">{{ mensagemBloqueioVSUP || 'Atualize o VSUC para 8.04a ou superior' }}</span>
                  </div>
                  <!-- SE SISTEMA JÁ ESTÁ ATUALIZADO: Mostrar mensagem de bloqueio -->
                  <div v-else-if="isAtualizado(sistema)" class="agendamento-bloqueado">
                    <v-chip
                      small
                      color="success"
                      text-color="white"
                      class="bloqueio-chip"
                    >
                      <v-icon left small>mdi-check-circle</v-icon>
                      Sistema atualizado
                    </v-chip>
                    <span class="bloqueio-texto">Agendamento indisponível</span>
                  </div>
                  
                  <!-- SE NÃO TEM agendamento E NÃO ESTÁ ATUALIZADO: Mostrar input para criar -->
                  <div v-else-if="!temAgendamento(sistema)" class="input-group">
                    <v-text-field
                      v-model="agendamentoForm[sistema.toLowerCase()].time"
                      type="time"
                      dense
                      hide-details
                      outlined
                      class="time-input"
                      :disabled="loadingAgendamento"
                      placeholder="HH:MM"
                      hint="Horário: 07:30 - 23:50"
                    ></v-text-field>
                    <v-btn
                      icon
                      small
                      color="primary"
                      class="ml-1"
                      :loading="loadingAgendamento && sistemaAgendando === sistema"
                      :disabled="!agendamentoForm[sistema.toLowerCase()].time || loadingAgendamento"
                      @click="adicionarAgendamento(sistema)"
                    >
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </div>
                  
                  <!-- SE TEM agendamento: Mostrar agendamento existente com botão de remover -->
                  <div v-else class="agendamento-existente">
                    <v-chip
                      color="orange"
                      text-color="white"
                      close
                      @click:close="removerAgendamento(sistema)"
                      class="agendamento-chip"
                    >
                      <v-icon left small>mdi-clock-outline</v-icon>
                      Hoje às {{ getAgendamentoHorario(sistema) }}
                    </v-chip>
                  </div>
                </div>
              </div>

              <!-- Linha única: Gravação de Vídeo, Ativar Tudo, Horário Mínimo, Sugestão e Salvar -->
              <div v-if="selectedHost && (selectedHost.hasVCON || selectedHost.hasVPRA || selectedHost.hasVSUC || selectedHost.hasVSUP || selectedHost.hasVSYN)" class="d-flex align-center justify-space-between pa-2 mt-0 mb-0 flex-wrap" style="gap: 12px;">
                <!-- Gravação de Vídeo (Cronograma) -->
                <div class="d-flex align-center">
                  <v-checkbox v-model="logsVideo" class="mr-3"></v-checkbox>
                  <div>
                    <div class="d-flex align-center">
                      <v-icon left color="red" small>mdi-video</v-icon>
                      <strong class="text-h6">Gravação de Vídeo (Cronograma)</strong>
                    </div>
                  </div>
                </div>
                
                <!-- Botão Ativar/Desativar Tudo -->
                <v-btn
                  :color="todosCronogramasAtivados ? 'error' : 'success'"
                  @click="toggleAtivarTudoCronogramas"
                  outlined
                  small
                >
                  <v-icon left>{{ todosCronogramasAtivados ? 'mdi-checkbox-multiple-marked' : 'mdi-checkbox-multiple-blank' }}</v-icon>
                  {{ todosCronogramasAtivados ? 'Desativar Tudo' : 'Ativar Tudo' }}
                </v-btn>
                
                <!-- Horário Mínimo e Sugestão de Horário -->
                <div class="d-flex align-center" style="gap: 8px;">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="horarioMinimo"
                        type="time"
                        label="Horário Mínimo"
                        outlined
                        dense
                        :error-messages="erroHorarioMinimo"
                        style="max-width: 150px;"
                        mask="##:##"
                        placeholder="HH:MM"
                        v-bind="attrs"
                        v-on="on"
                        @blur="validarHorarioMinimo"
                        hide-details="auto"
                      ></v-text-field>
                    </template>
                    <span>Defina um horário de ao menos 30 minutos a mais após fim do horário de backup</span>
                  </v-tooltip>
                  <v-btn
                    color="primary"
                    @click="aplicarSugestaoHorarios"
                    :loading="carregandoSugestao"
                    :disabled="!temSistemasCronogramaSelecionados || !!erroHorarioMinimo"
                    style="height: 40px;"
                  >
                    <v-icon left>mdi-lightbulb-on</v-icon>
                    Sugestão de Horário
                  </v-btn>
                </div>
                
                <!-- Botão Salvar Cronogramas -->
                <v-btn
                  color="primary"
                  :loading="salvandoCronograma"
                  :disabled="salvandoCronograma || horariosConflitantes.length > 0"
                  @click="salvarCronogramas"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Salvar Cronogramas
                </v-btn>
              </div>

              <!-- Alerta de conflitos de horário -->
              <v-alert v-if="horariosConflitantes.length > 0" type="error" outlined class="mt-1">
                <div class="d-flex align-center">
                  <v-icon left>mdi-alert-circle</v-icon>
                  <strong>Conflito de Horários Detectado!</strong>
                </div>
                <ul class="mt-2">
                  <li v-for="(conflito, index) in horariosConflitantes" :key="index">
                    {{ conflito }}
                  </li>
                </ul>
                <div class="mt-2">
                  <small>
                    <strong>Regra:</strong> Deve haver no mínimo 10 minutos entre a execução de sistemas diferentes
                  </small>
                </div>
              </v-alert>
            </div>
          </v-card-text>
        </v-tab-item>

        <!-- ABA 2: CONFIGURAÇÃO -->
        <v-tab-item class="tab-content-area">
          <v-card-text class="pa-2" :style="cardTextStyle">
            <h3 class="mb-4">Segurança e Conectividade</h3>
            <div class="config-grid">
              <v-card class="config-card" outlined>
                <v-card-text>
                  <div class="d-flex align-center justify-space-between">
                    <div class="config-info">
                      <h4>Bloqueio Geral de Porta</h4>
                      <p class="caption grey--text">Impede qualquer conexão externa durante as atualizações.</p>
                    </div>
                    <v-switch
                      v-model="portaConfigForm.porta_bloqueio"
                      dense
                      hide-details
                      :disabled="salvandoPortaConfig"
                    ></v-switch>
                  </div>
                </v-card-text>
              </v-card>

              <v-card class="config-card" outlined>
                <v-card-text>
                  <div class="d-flex align-center justify-space-between">
                    <div class="config-info">
                      <h4>Forçar Porta Liberada</h4>
                      <p class="caption grey--text">Mantém a porta aberta mesmo em caso de falha.</p>
                    </div>
                    <v-switch
                      v-model="portaConfigForm.porta_liberado"
                      dense
                      hide-details
                      :disabled="salvandoPortaConfig"
                    ></v-switch>
                  </div>
                </v-card-text>
              </v-card>
            </div>

            <v-card-actions class="mt-4 px-0">
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                :loading="salvandoPortaConfig"
                :disabled="salvandoPortaConfig"
                @click="salvarPortaConfig"
              >
                <v-icon left>mdi-content-save</v-icon>
                Salvar Configurações
              </v-btn>
            </v-card-actions>
          </v-card-text>
        </v-tab-item>

        <!-- ABA 3: LOGS -->
        <v-tab-item class="tab-content-area">
          <v-card-text class="pa-2" :style="cardTextStyle">
            <div v-if="loadingLogs" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2">Carregando logs...</p>
            </div>
            
            <v-data-table
              v-else
              :headers="logHeaders"
              :items="atualizacoes"
              :items-per-page="10"
              class="log-table elevation-1"
              dense
            >
              <template v-slot:item.id="{ item }">
                <v-chip
                  x-small
                  outlined
                  :color="item.metodo === 'CRONOGRAMA_RETRY' ? undefined : getCorMetodo(item.metodo)"
                  :style="getChipStyle(item.metodo)"
                  class="font-weight-medium"
                >
                  {{ item.id }}
                </v-chip>
              </template>

              <template v-slot:item.sistema="{ item }">
                <div class="sistema-logo-container">
                  <img 
                    :src="getIconeSistema(item.sistema)" 
                    :alt="item.sistema"
                    :title="item.sistema"
                    class="sistema-logo-rounded"
                  />
                </div>
              </template>

              <template v-slot:item.base_size="{ item }">
                {{ formatBaseSize(item.base_size) }}
              </template>

              <template v-slot:item.metodo="{ item }">
                <v-chip
                  small
                  :color="item.metodo === 'CRONOGRAMA_RETRY' ? undefined : getCorMetodo(item.metodo)"
                  :style="getChipStyle(item.metodo)"
                  outlined
                >
                  {{ item.metodo }}
                </v-chip>
              </template>

              <template v-slot:item.versao_atual="{ item }">
                <span class="caption">{{ item.versao_atual || 'N/A' }}</span>
              </template>

              <template v-slot:item.versao_final="{ item }">
                <span class="caption font-weight-bold">{{ item.versao_final || 'N/A' }}</span>
              </template>

              <template v-slot:item.atualizado="{ item }">
                <v-chip
                  x-small
                  :color="item.atualizado ? 'success' : 'error'"
                  text-color="white"
                >
                  {{ item.atualizado ? 'Sucesso' : 'Falha' }}
                </v-chip>
              </template>

              <template v-slot:item.started_at="{ item }">
                <span class="caption">{{ formatDate(item.started_at) }}</span>
              </template>

              <template v-slot:item.duracao="{ item }">
                <span class="caption">{{ formatDuracao(item.duracao) }}</span>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="abrirLogViewer(item)"
                >
                  <v-icon>mdi-file-eye</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>

      <!-- Footer com botão de fechar -->
      <v-divider></v-divider>
      <v-card-actions class="px-6 py-3">
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog">
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Modal interno para visualizar log -->
    <v-dialog v-model="logViewerDialog" max-width="1000px" scrollable>
      <v-card class="log-modal-card">
        <v-card-title class="log-header-bar">
          <div class="log-title-group">
            <div class="log-type-title">
              <v-icon left color="white">
                {{ selectedLogItem && selectedLogItem.metodo === 'MANUAL' ? 'mdi-hand-pointing-up' : 'mdi-cog' }}
              </v-icon>
              {{ selectedLogItem && selectedLogItem.metodo ? selectedLogItem.metodo : 'LOG' }}
            </div>
            <div class="log-sub-info" v-if="selectedLogItem">
              ID: {{ selectedLogItem.id }} | HostID: {{ selectedLogItem.hostid }} | Host: {{ selectedHost ? selectedHost.name : '' }} | Sistema: {{ selectedLogItem.sistema }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <!-- Botão de vídeo -->
          <v-btn 
            icon 
            dark
            :color="hasVideo ? 'success' : 'grey'" 
            @click="abrirVideoDialog"
            title="Ver vídeo"
            :disabled="!hasVideo"
            class="mr-2"
          >
            <v-icon>mdi-video</v-icon>
          </v-btn>
          <v-btn icon dark @click="logViewerDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text :style="selectedLogFile && selectedLogFile.isVideo ? 'height: 700px;' : 'height: 500px;'">
          <div v-if="loadingLogContent" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-2">Carregando conteúdo...</p>
          </div>
          
          <div v-else-if="selectedLogFile">
            <!-- Exibir imagem -->
            <div v-if="selectedLogFile.isImage" class="text-center">
              <img 
                :src="selectedLogFile.url" 
                :alt="selectedLogFile.nome"
                style="max-width: 100%; height: auto;"
              />
            </div>
            
            <!-- Exibir vídeo -->
            <div v-else-if="selectedLogFile.isVideo" class="text-center">
              <video 
                :src="selectedLogFile.url" 
                controls
                style="max-width: 100%; height: auto;"
                preload="metadata"
                @error="onVideoError"
              >
                Seu navegador não suporta a reprodução de vídeos.
              </video>
            </div>
            
            <!-- Exibir conteúdo de texto -->
            <div v-else>
              <pre class="log-content" v-html="formatLogContent(selectedLogFile.conteudo || selectedLogContent)"></pre>
            </div>
          </div>
          
          <!-- Fallback para exibição de texto quando não há arquivo de mídia -->
          <pre v-else-if="selectedLogContent" class="log-content" v-html="formatLogContent(selectedLogContent)"></pre>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="log-footer-bar">
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="logViewerDialog = false">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para visualizar vídeo -->
    <v-dialog v-model="videoDialog" max-width="1000px" persistent>
      <v-card class="log-modal-card">
        <v-card-title class="log-header-bar">
          <div class="log-title-group">
            <div class="log-type-title">
              <v-icon left class="mr-2">mdi-video</v-icon>
              Vídeo de Atualização
            </div>
            <div class="log-sub-info" v-if="selectedLogItem">
              ID: {{ selectedLogItem.id }} | Sistema: {{ selectedLogItem.sistema }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="videoDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text style="height: 700px;">
          <div v-if="selectedVideo" class="text-center">
            <video 
              :src="selectedVideo.url" 
              controls
              style="max-width: 100%; height: auto; max-height: 650px;"
              preload="metadata"
              @error="onVideoError"
            >
              Seu navegador não suporta a reprodução de vídeos.
            </video>
            <p class="mt-2 caption grey--text">{{ selectedVideo.nome }}</p>
          </div>
          <div v-else class="text-center py-8">
            <v-icon size="64" color="grey lighten-1">mdi-video-off</v-icon>
            <p class="mt-2 grey--text">Nenhum vídeo selecionado</p>
          </div>
        </v-card-text>
        
        <v-divider></v-divider>
        <v-card-actions class="log-footer-bar">
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="videoDialog = false">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import hostsService from '@/services/hostsService'
import dashboardService from '@/services/dashboardService'
import { formatarLog } from '@/utils/formatterlog'
import HorarioValidator from '@/utils/HorarioValidator.js'
import configuracaoScoreService from '@/services/configuracaoScoreService'
import authService from '@/services/authService'
import axios from 'axios'
import { getApiUrl, getDefaultHeaders } from '@/config/env'

export default {
  name: 'UnifiedActionDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    selectedHost: {
      type: Object,
      default: null
    },
    latestVersions: {
      type: Object,
      default: () => ({})
    },
    atualizando: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      activeTab: 0,
      sistemasDisponiveis: ['VCON', 'VPRA', 'VSUC', 'VSUP', 'VSYN'],
      
      // Formulário de cronograma
      cronogramaForm: {
        vcon: { enabled: false, time: '00:00' },
        vpra: { enabled: false, time: '00:00' },
        vsuc: { enabled: false, time: '00:00' },
        vsup: { enabled: false, time: '00:00' },
        vsyn: { enabled: false, time: '00:00' }
      },
      loadingCronograma: false,
      salvandoCronograma: false,
      logsVideo: false, // Gravação de vídeo para cronogramas
      horarioMinimo: '', // Horário mínimo para sugestão
      erroHorarioMinimo: '', // Erro de validação do horário mínimo
      carregandoSugestao: false, // Loading ao aplicar sugestão de horários

      // Formulário de agendamento (apenas horário - sempre executa hoje)
      agendamentoForm: {
        vcon: { time: '' },
        vpra: { time: '' },
        vsuc: { time: '' },
        vsup: { time: '' },
        vsyn: { time: '' }
      },
      agendamentosExistentes: {
        vcon: [],
        vpra: [],
        vsuc: [],
        vsup: [],
        vsyn: []
      },
      loadingAgendamento: false,
      sistemaAgendando: null,

      // Configuração de porta
      portaConfigForm: {
        porta_bloqueio: false,
        porta_liberado: false
      },
      salvandoPortaConfig: false,

      // Logs
      logHeaders: [
        { text: 'ID', value: 'id', width: '80px' },
        { text: 'Sistema', value: 'sistema', width: '100px' },
        { text: 'Base(GB)', value: 'base_size', width: '100px' },
        { text: 'Método', value: 'metodo', width: '120px' },
        { text: 'Versão Atual', value: 'versao_atual', width: '120px' },
        { text: 'Versão Final', value: 'versao_final', width: '120px' },
        { text: 'Status', value: 'atualizado', width: '100px' },
        { text: 'Iniciado em', value: 'started_at', width: '150px' },
        { text: 'Duração', value: 'duracao', width: '100px' },
        { text: 'Ações', value: 'actions', width: '80px', sortable: false }
      ],
      atualizacoes: [],
      loadingLogs: false,
      logViewerDialog: false,
      selectedLogItem: null,
      selectedLogContent: '',
      loadingLogContent: false,
      selectedLogFile: null, // Para armazenar informações de arquivo de mídia (vídeo/imagem)
      videosDisponiveis: [], // Lista de vídeos disponíveis para a atualização atual
      videoDialog: false, // Dialog para exibir vídeo
      selectedVideo: null, // Vídeo selecionado para exibição
      
      // Validações
      horariosConflitantes: [],
      templateValidationError: false,
      templateValidationMessage: '',
      
      // Scores de segurança
      scoresSistemas: {},
      carregandoScores: false,
      scoresJaBuscados: false,
      configuracoesScore: {
        SCORE_LIMITE_ACEITO: 0,
        SCORE_LIMITE_BLOQUEIO: 0,
        DIAS_LIMITE_BLOQUEIO: 0
      },

      // Regra VSUP: só pode atualizar VSUP se VSUC estiver em 8.04a ou superior
      podeAtualizarVSUP: true,
      mensagemBloqueioVSUP: ''
    }
  },

  computed: {
    internalDialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    
    // Verificar se há vídeo disponível
    hasVideo() {
      return this.videosDisponiveis.length > 0
    },
    
    // Verificar se todos os cronogramas estão ativados
    todosCronogramasAtivados() {
      if (!this.selectedHost) return false
      
      const sistemasDisponiveis = []
      if (this.selectedHost.hasVCON) sistemasDisponiveis.push('vcon')
      if (this.selectedHost.hasVPRA) sistemasDisponiveis.push('vpra')
      if (this.selectedHost.hasVSUC) sistemasDisponiveis.push('vsuc')
      if (this.selectedHost.hasVSUP) sistemasDisponiveis.push('vsup')
      if (this.selectedHost.hasVSYN) sistemasDisponiveis.push('vsyn')
      
      if (sistemasDisponiveis.length === 0) return false
      
      return sistemasDisponiveis.every(sistema => {
        return this.cronogramaForm[sistema].enabled === true
      })
    },
    
    // Verificar se há sistemas com cronograma selecionados
    temSistemasCronogramaSelecionados() {
      return this.cronogramaForm.vcon.enabled || 
             this.cronogramaForm.vpra.enabled || 
             this.cronogramaForm.vsuc.enabled || 
             this.cronogramaForm.vsup.enabled || 
             this.cronogramaForm.vsyn.enabled
    },
    
    // Contar quantidade de sistemas disponíveis para o host
    quantidadeSistemas() {
      if (!this.selectedHost) return 0
      
      let count = 0
      if (this.selectedHost.hasVCON) count++
      if (this.selectedHost.hasVPRA) count++
      if (this.selectedHost.hasVSUC) count++
      if (this.selectedHost.hasVSUP) count++
      if (this.selectedHost.hasVSYN) count++
      
      return count
    },
    
    // Calcular altura do dialog baseada na quantidade de sistemas
    dialogStyle() {
      if (!this.selectedHost || this.quantidadeSistemas === 0) {
        return {}
      }
      
      // Baseado nos valores observados:
      // 5 sistemas = 804px, 3 sistemas = 566px
      // Diferença: 238px para 2 sistemas = 119px por sistema
      // Base: 804 - (5 * 119) = 209px ou 566 - (3 * 119) = 209px
      
      // Altura base do dialog (header tabs + footer + padding do dialog)
      const alturaBase = 209
      
      // Altura de cada system-row (119px cada, baseado na diferença observada)
      const alturaPorSistema = 119
      
      // Calcular altura total
      const alturaTotal = alturaBase + (this.quantidadeSistemas * alturaPorSistema)
      
      return {
        height: `${alturaTotal}px`,
        maxHeight: `${alturaTotal}px`
      }
    },
    
    // Estilo para o card-text (altura fixa baseada na quantidade de sistemas)
    cardTextStyle() {
      if (!this.selectedHost || this.quantidadeSistemas === 0) {
        return { maxHeight: '750px', overflowY: 'auto' }
      }
      
      // Altura do header tabs
      const alturaTabs = 48
      
      // Altura do footer (v-card-actions)
      const alturaFooter = 64
      
      // Altura de cada system-row (119px cada)
      const alturaPorSistema = 119
      
      // Altura da linha de configurações (gravação de vídeo, ativar tudo, etc.)
      const alturaConfiguracoes = 80
      
      // Altura do padding interno (pa-2 = 8px top + 8px bottom = 16px)
      const alturaPadding = 16
      
      // Calcular altura do conteúdo
      // Altura total do dialog - tabs - footer = conteúdo disponível
      const alturaTotalDialog = 209 + (this.quantidadeSistemas * 119)
      const alturaConteudo = alturaTotalDialog - alturaTabs - alturaFooter
      
      return {
        height: `${alturaConteudo}px`,
        maxHeight: `${alturaConteudo}px`,
        overflowY: 'auto'
      }
    }
  },

  watch: {
    value(newVal) {
      if (newVal && this.selectedHost) {
        // Sempre resetar para aba 0 (Atualizações) ao abrir o dialog
        this.activeTab = 0
        
        // IMPORTANTE: Resetar dados limpa os valores, mas loadInitialData vai recarregá-los
        this.resetDialogData()
        
        // Calcular e preencher horário mínimo baseado em horario_FimBackup
        if (this.selectedHost.horario_FimBackup) {
          const horarioMinimoCalculado = HorarioValidator.calcularHorarioMinimo(this.selectedHost.horario_FimBackup)
          if (horarioMinimoCalculado) {
            this.horarioMinimo = horarioMinimoCalculado
          }
        }
        
        // Carregar dados do host atual (incluindo porta config)
        this.loadInitialData()
        
        // Carregar configurações de score se ainda não foram carregadas
        if (!this.configuracoesScore.SCORE_LIMITE_ACEITO) {
          this.carregarConfiguracoesScore()
        }
        
        // Buscar scores de segurança quando o diálogo é aberto (apenas uma vez)
        if (!this.scoresJaBuscados) {
          this.buscarScoresSeguranca(this.selectedHost)
          this.scoresJaBuscados = true
        }
        // Verificar se pode atualizar VSUP (regra: VSUC 8.04a ou superior)
        this.carregarPodeAtualizarVSUP()
      } else if (!newVal) {
        // Resetar flag quando o diálogo é fechado
        this.scoresJaBuscados = false
      }
    },
    selectedHost(newHost, oldHost) {
      // Se mudou de host e o dialog está aberto, recarregar
      if (newHost && oldHost && newHost.hostId !== oldHost.hostId && this.value) {
        this.activeTab = 0
        this.resetDialogData()
        this.loadInitialData()
        
        // Resetar flag para buscar scores do novo host
        this.scoresJaBuscados = false
        if (newHost) {
          this.buscarScoresSeguranca(newHost)
          this.scoresJaBuscados = true
        }
        this.carregarPodeAtualizarVSUP()
      }
    },
    activeTab(newTab) {
      if (!this.selectedHost) return
      
      // Quando trocar para a aba de Configuração (índice 1), carregar porta config
      if (newTab === 1) {
        this.loadPortaConfig()
      }
      
      // Quando trocar para a aba de Logs (índice 2), carregar os dados
      if (newTab === 2 && this.atualizacoes.length === 0) {
        this.loadLogs()
      }
    },
    
    logViewerDialog(newVal) {
      // Limpar URLs temporárias quando o diálogo de log for fechado
      if (!newVal) {
        this.cleanupTempUrl()
        this.selectedLogFile = null
        this.selectedLogContent = ''
        this.videosDisponiveis = []
      }
    },
    
    videoDialog(newVal) {
      // Limpar URLs temporárias quando o diálogo de vídeo for fechado
      if (!newVal) {
        if (this.selectedVideo && this.selectedVideo.url) {
          URL.revokeObjectURL(this.selectedVideo.url)
        }
        this.selectedVideo = null
      }
    },
    // Watchers para validar horários em tempo real
    'cronogramaForm.vcon.time': { handler() { this.validarHorariosCronograma() } },
    'cronogramaForm.vpra.time': { handler() { this.validarHorariosCronograma() } },
    'cronogramaForm.vsuc.time': { handler() { this.validarHorariosCronograma() } },
    'cronogramaForm.vsup.time': { handler() { this.validarHorariosCronograma() } },
    'cronogramaForm.vsyn.time': { handler() { this.validarHorariosCronograma() } },
    'cronogramaForm.vcon.enabled': { handler() { this.validarHorariosCronograma() } },
    'cronogramaForm.vpra.enabled': { handler() { this.validarHorariosCronograma() } },
    'cronogramaForm.vsuc.enabled': { handler() { this.validarHorariosCronograma() } },
    'cronogramaForm.vsup.enabled': { handler() { this.validarHorariosCronograma() } },
    'cronogramaForm.vsyn.enabled': { handler() { this.validarHorariosCronograma() } }
  },
  
  mounted() {
    // Carregar configurações de score quando o componente é montado
    this.carregarConfiguracoesScore()
  },
  
  beforeDestroy() {
    // Limpar URLs temporárias quando o componente for destruído
    this.cleanupTempUrl()
  },
  
  methods: {
    resetDialogData() {
      // Limpar atualizações/logs
      this.atualizacoes = []
      
      // Resetar formulários
      this.sistemasDisponiveis.forEach(sistema => {
        const key = sistema.toLowerCase()
        this.cronogramaForm[key] = { enabled: false, time: '00:00' }
        this.agendamentoForm[key] = { time: '' }
        this.agendamentosExistentes[key] = []
      })
      
      // Resetar configurações de porta (serão recarregadas no loadInitialData)
      this.portaConfigForm = {
        porta_bloqueio: false,
        porta_liberado: false
      }
      
      // Fechar log viewer se estiver aberto
      this.cleanupTempUrl()
      this.logViewerDialog = false
      this.selectedLogItem = null
      this.selectedLogContent = ''
      this.selectedLogFile = null
      
      // Resetar scores
      this.scoresSistemas = {}
      this.scoresJaBuscados = false

      // Resetar regra VSUP
      this.podeAtualizarVSUP = true
      this.mensagemBloqueioVSUP = ''

      // Resetar configurações de cronograma
      this.logsVideo = false
      this.horarioMinimo = ''
      this.erroHorarioMinimo = ''
      this.carregandoSugestao = false
      
      // Calcular horário mínimo se houver horario_FimBackup
      if (this.selectedHost && this.selectedHost.horario_FimBackup) {
        const horarioMinimoCalculado = HorarioValidator.calcularHorarioMinimo(this.selectedHost.horario_FimBackup)
        if (horarioMinimoCalculado) {
          this.horarioMinimo = horarioMinimoCalculado
        }
      }
    },

    async loadInitialData() {
      if (!this.selectedHost) return

      // SEMPRE carregar configurações de porta (necessário para validações)
      this.loadPortaConfig()

      // Carregar dados baseado na aba ativa
      if (this.activeTab === 0) {
        await Promise.all([
          this.loadCronograma(),
          this.loadAgendamentos()
        ])
      } else if (this.activeTab === 1) {
        // Porta config já foi carregada acima
      } else if (this.activeTab === 2) {
        await this.loadLogs()
      }
    },

    async loadCronograma() {
      if (!this.selectedHost) return
      
      this.loadingCronograma = true
      try {
        const response = await hostsService.getCronogramaByHostId(this.selectedHost.hostId)
        
        if (response) {
          // API retorna um objeto único de cronograma, não um array
          // Formato: { id, hostId, vpra, vsup, vsuc, vcon, vsyn, horarioVpra, horarioVsuc, ... }
          
          // Resetar formulário
          this.sistemasDisponiveis.forEach(sistema => {
            const key = sistema.toLowerCase()
            this.cronogramaForm[key] = { enabled: false, time: '00:00' }
          })

          // Preencher com dados existentes
          this.sistemasDisponiveis.forEach(sistema => {
            const key = sistema.toLowerCase()
            // Gerar chave no formato camelCase: horarioVpra, horarioVsuc, etc.
            const horarioKey = `horario${sistema.charAt(0).toUpperCase()}${sistema.slice(1).toLowerCase()}`
            
            // Verificar se o sistema está ativo (campo booleano)
            const isEnabled = response[key] === true || response[key] === 'true'
            
            // Obter horário (formato HH:MM:SS, converter para HH:MM)
            let horario = '00:00'
            if (response[horarioKey]) {
              horario = response[horarioKey].substring(0, 5)
            }
            
            this.cronogramaForm[key] = {
              enabled: isEnabled,
              time: horario
            }
          })
          
          // Carregar logsVideo do cronograma existente
          if (response.logsVideo !== undefined) {
            this.logsVideo = response.logsVideo === true || response.logsVideo === 'true'
          }
        }
      } catch (error) {
        console.error('Erro ao carregar cronograma:', error)
        
        // Se erro 404, significa que não há cronograma cadastrado ainda (normal)
        if (error.response?.status !== 404) {
          this.$emit('show-snackbar', 'Erro ao carregar cronograma', 'error')
        }
      } finally {
        this.loadingCronograma = false
      }
    },

    async loadAgendamentos() {
      if (!this.selectedHost) return

      try {
        const response = await hostsService.getManualCronogramado(this.selectedHost.hostId)
        
        // Resetar agendamentos
        this.sistemasDisponiveis.forEach(sistema => {
          const key = sistema.toLowerCase()
          this.agendamentosExistentes[key] = []
        })

        // A API retorna diretamente um array, não um objeto { success, agendamentos }
        if (response && Array.isArray(response) && response.length > 0) {
          response.forEach(agendamento => {
            const sistema = agendamento.sistema.toLowerCase()
            if (this.agendamentosExistentes[sistema]) {
              this.agendamentosExistentes[sistema].push(agendamento)
            }
          })
        }
      } catch (error) {
        console.error('Erro ao carregar agendamentos:', error)
        this.$emit('show-snackbar', 'Erro ao carregar agendamentos', 'error')
      }
    },

    loadPortaConfig() {
      if (!this.selectedHost) return

      this.portaConfigForm = {
        porta_bloqueio: this.selectedHost.porta_bloqueio === true || this.selectedHost.porta_bloqueio === 'true',
        porta_liberado: this.selectedHost.porta_liberado === true || this.selectedHost.porta_liberado === 'true'
      }
    },

    async loadLogs() {
      if (!this.selectedHost) return

      this.loadingLogs = true
      try {
        const response = await hostsService.getAtualizacoesByHostId(this.selectedHost.hostId)
        
        if (response && response.data && Array.isArray(response.data)) {
          this.atualizacoes = response.data
        } else {
          this.atualizacoes = []
        }
      } catch (error) {
        console.error('Erro ao carregar histórico de atualizações:', error)
        this.$emit('show-snackbar', 'Erro ao carregar histórico de atualizações', 'error')
        this.atualizacoes = []
      } finally {
        this.loadingLogs = false
      }
    },

    async abrirLogViewer(item) {
      this.selectedLogItem = item
      this.logViewerDialog = true
      this.loadingLogContent = true
      this.selectedLogContent = ''
      this.selectedLogFile = null
      this.videosDisponiveis = []

      try {
        // Buscar vídeos relacionados a esta atualização
        await this.buscarVideosPorId(item.id)
        
        // Verificar se há arquivos de vídeo/imagem relacionados
        // Primeiro, tentar buscar logs organizados por sistema/data (como no LogViewer)
        try {
          const hostId = this.selectedHost?.hostId || this.selectedHost?.id
          if (hostId) {
            const logsResponse = await hostsService.getLogsByHostId(hostId)
            
            if (logsResponse && logsResponse.success && logsResponse.logs) {
              // Procurar arquivos de vídeo/imagem relacionados a esta atualização
              const sistema = item.sistema
              const dataAtualizacao = item.started_at ? new Date(item.started_at).toISOString().split('T')[0] : null
              
              if (dataAtualizacao) {
                // Buscar arquivos de mídia para este sistema e data
                // O método buscarArquivoMidia já verifica se é manual ou cronograma
                const arquivoMidia = this.buscarArquivoMidia(logsResponse.logs, sistema, dataAtualizacao)
                
                if (arquivoMidia) {
                  // Carregar arquivo de mídia usando o sistema completo (AttManual/Sistema ou Sistema)
                  const sistemaCompleto = arquivoMidia.sistemaCompleto || sistema
                  await this.carregarArquivoMidia(hostId, sistemaCompleto, dataAtualizacao, arquivoMidia)
                  // Se encontrou arquivo de mídia, não precisa carregar o log de texto
                  this.loadingLogContent = false
                  return
                }
              }
            }
          }
        } catch (logsError) {
          console.log('Não foi possível buscar logs organizados, usando log de texto:', logsError)
          // Continuar com o carregamento do log de texto
        }
        
        // Buscar log da atualização usando o ID da atualização (fallback para texto)
        const response = await dashboardService.getLogAtualizacao(item.id)
        
        let logs = null
        if (response && response.controle && response.controle.logs) {
          logs = response.controle.logs
        } else if (response && response.logs) {
          logs = response.logs
        }
        
        if (logs) {
          this.selectedLogContent = logs
          this.selectedLogFile = {
            nome: `log_${item.id}.txt`,
            conteudo: logs,
            isImage: false,
            isVideo: false
          }
        } else {
          this.selectedLogContent = 'Conteúdo da log não disponível.'
        }
      } catch (error) {
        console.error('Erro ao visualizar log:', error)
        this.selectedLogContent = 'Erro ao carregar conteúdo da log.'
        this.$emit('show-snackbar', 'Erro ao visualizar log', 'error')
      } finally {
        this.loadingLogContent = false
      }
    },
    
    // Buscar arquivo de mídia (vídeo/imagem) nos logs organizados
    buscarArquivoMidia(logs, sistema, data) {
      if (!logs || !logs.sistemas) return null
      
      // Primeiro, tentar encontrar em sistemas manuais (AttManual)
      for (const sistemaLog of logs.sistemas) {
        if (sistemaLog.tipo === 'manual' && sistemaLog.subSistemas) {
          // Estrutura hierárquica (AttManual)
          for (const subSistema of sistemaLog.subSistemas) {
            if (subSistema.sistema === sistema && subSistema.datas) {
              const dataLog = subSistema.datas.find(d => d.data === data)
              if (dataLog && dataLog.arquivos) {
                // Procurar arquivo de vídeo ou imagem
                const arquivoMidia = dataLog.arquivos.find(arquivo => {
                  const nome = arquivo.nome.toLowerCase()
                  return nome.endsWith('.mp4') || nome.endsWith('.png') || nome.endsWith('.jpg') || nome.endsWith('.jpeg')
                })
                if (arquivoMidia) {
                  // Retornar com informação do sistema completo (AttManual/Sistema)
                  return {
                    ...arquivoMidia,
                    sistemaCompleto: `AttManual/${subSistema.sistema}`
                  }
                }
              }
            }
          }
        }
      }
      
      // Se não encontrou em sistemas manuais, procurar em sistemas normais (cronograma)
      const sistemaLog = logs.sistemas.find(s => s.sistema === sistema && s.tipo !== 'manual')
      if (sistemaLog && sistemaLog.datas) {
        const dataLog = sistemaLog.datas.find(d => d.data === data)
        if (dataLog && dataLog.arquivos) {
          // Procurar arquivo de vídeo ou imagem
          const arquivoMidia = dataLog.arquivos.find(arquivo => {
            const nome = arquivo.nome.toLowerCase()
            return nome.endsWith('.mp4') || nome.endsWith('.png') || nome.endsWith('.jpg') || nome.endsWith('.jpeg')
          })
          if (arquivoMidia) {
            return {
              ...arquivoMidia,
              sistemaCompleto: sistemaLog.sistema
            }
          }
        }
      }
      
      return null
    },
    
    // Carregar arquivo de mídia (vídeo/imagem)
    async carregarArquivoMidia(hostId, sistema, data, arquivo) {
      // Verificar se o usuário está autenticado
      if (!authService.isAuthenticated()) {
        this.$emit('show-snackbar', 'Você não está autenticado. Faça login novamente.', 'error')
        return
      }
      
      try {
        const token = localStorage.getItem('authToken')
        
        if (!token) {
          this.$emit('show-snackbar', 'Token de autenticação não encontrado. Faça login novamente.', 'error')
          return
        }
        
        const isImage = arquivo.nome.endsWith('.png') || arquivo.nome.endsWith('.jpg') || arquivo.nome.endsWith('.jpeg')
        const isVideo = arquivo.nome.endsWith('.mp4')
        
        if (isVideo || isImage) {
          // Para vídeos e imagens, fazer requisição direta com axios e token Bearer no header
          const encodedSistema = encodeURIComponent(sistema)
          const encodedData = encodeURIComponent(data)
          const encodedFileName = encodeURIComponent(arquivo.nome)
          
          const fullUrl = getApiUrl(`/hosts/${hostId}/logs/${encodedSistema}/${encodedData}/${encodedFileName}`)
          
          const requestConfig = {
            headers: {
              ...getDefaultHeaders(),
              'Authorization': `Bearer ${token}`,
              'Accept': isVideo ? 'video/mp4' : 'image/*'
            },
            responseType: 'blob',
            timeout: 30000
          }
          
          try {
            const response = await axios.get(fullUrl, requestConfig)
            
            // Criar URL temporária para o blob
            const mimeType = isVideo ? 'video/mp4' : this.getImageMimeType(arquivo.nome)
            const mediaBlob = new Blob([response.data], { type: mimeType })
            const mediaUrl = URL.createObjectURL(mediaBlob)
            
            this.selectedLogFile = {
              nome: arquivo.nome,
              url: mediaUrl,
              isImage: isImage,
              isVideo: isVideo,
              conteudo: null
            }
          } catch (requestError) {
            console.error('Erro na requisição de arquivo de mídia:', requestError)
            
            if (requestError.response?.status === 401) {
              this.$emit('show-snackbar', 'Sessão expirada. Faça login novamente.', 'error')
              return
            } else {
              throw requestError
            }
          }
        } else {
          // Para outros tipos de arquivo, usar o serviço normal
          const response = await hostsService.getLogFile(hostId, sistema, data, arquivo.nome)
          
          if (response && response.success) {
            this.selectedLogFile = {
              nome: arquivo.nome,
              url: response.url,
              isImage: false,
              isVideo: false,
              conteudo: response.conteudo
            }
          } else {
            throw new Error(response.message || 'Erro ao visualizar arquivo')
          }
        }
      } catch (error) {
        console.error('Erro ao carregar arquivo de mídia:', error)
        let errorMessage = 'Erro ao carregar arquivo de mídia'
        if (error.response?.status === 401) {
          errorMessage = 'Não autorizado a acessar este arquivo. Verifique sua autenticação.'
        } else if (error.response?.status === 404) {
          errorMessage = 'Arquivo não encontrado no servidor.'
        } else if (error.message) {
          errorMessage = error.message
        }
        this.$emit('show-snackbar', errorMessage, 'error')
      }
    },
    
    getImageMimeType(fileName) {
      const extension = fileName.toLowerCase().split('.').pop()
      switch (extension) {
        case 'png':
          return 'image/png'
        case 'jpg':
        case 'jpeg':
          return 'image/jpeg'
        default:
          return 'image/png'
      }
    },
    
    onVideoError(event) {
      console.error('[VIDEO] Erro no vídeo:', event)
      console.error('[VIDEO] Detalhes do erro:', event.target.error)
      this.$emit('show-snackbar', 'Erro ao reproduzir vídeo. Verifique se o arquivo está disponível.', 'error')
    },
    
    cleanupTempUrl() {
      if (this.selectedLogFile && (this.selectedLogFile.isVideo || this.selectedLogFile.isImage) && this.selectedLogFile.url) {
        URL.revokeObjectURL(this.selectedLogFile.url)
      }
      if (this.selectedVideo && this.selectedVideo.url) {
        URL.revokeObjectURL(this.selectedVideo.url)
      }
    },
    
    // Buscar vídeos relacionados a uma atualização por ID
    async buscarVideosPorId(atualizacaoId) {
      if (!this.selectedHost || !this.selectedLogItem) return
      
      try {
        const hostId = this.selectedHost.hostId || this.selectedHost.id
        const logsResponse = await hostsService.getLogsByHostId(hostId)
        
        if (logsResponse && logsResponse.success && logsResponse.logs && logsResponse.logs.sistemas) {
          const videosEncontrados = []
          const sistemaAtualizacao = this.selectedLogItem.sistema
          const dataAtualizacao = this.selectedLogItem.started_at ? new Date(this.selectedLogItem.started_at).toISOString().split('T')[0] : null
          const metodoAtualizacao = this.selectedLogItem.metodo
          
          // Determinar se é atualização manual ou cronograma
          const isManual = metodoAtualizacao === 'MANUAL' || metodoAtualizacao === 'MANUAL_CRONOGRAMA'
          
          // Procurar vídeos nos sistemas
          for (const sistemaLog of logsResponse.logs.sistemas) {
            // Verificar se é sistema manual (tem subSistemas e tipo === 'manual')
            if (isManual && sistemaLog.tipo === 'manual' && sistemaLog.subSistemas) {
              // Estrutura hierárquica (AttManual)
              for (const subSistema of sistemaLog.subSistemas) {
                // Verificar se o subsistema corresponde ao sistema da atualização
                if (subSistema.sistema === sistemaAtualizacao && subSistema.datas) {
                  for (const dataLog of subSistema.datas) {
                    // Verificar se a data corresponde
                    if (dataAtualizacao && dataLog.data === dataAtualizacao && dataLog.arquivos) {
                      const videos = dataLog.arquivos.filter(arquivo => 
                        arquivo.nome.toLowerCase().endsWith('.mp4')
                      )
                      videos.forEach(video => {
                        video.sistema = `AttManual/${subSistema.sistema}`
                        video.data = dataLog.data
                        video.atualizacaoId = atualizacaoId
                      })
                      videosEncontrados.push(...videos)
                    }
                  }
                }
              }
            } else if (!isManual && sistemaLog.sistema === sistemaAtualizacao) {
              // Estrutura normal (cronograma) - verificar se o sistema corresponde
              if (sistemaLog.datas) {
                for (const dataLog of sistemaLog.datas) {
                  // Verificar se a data corresponde
                  if (dataAtualizacao && dataLog.data === dataAtualizacao && dataLog.arquivos) {
                    const videos = dataLog.arquivos.filter(arquivo => 
                      arquivo.nome.toLowerCase().endsWith('.mp4')
                    )
                    videos.forEach(video => {
                      video.sistema = sistemaLog.sistema
                      video.data = dataLog.data
                      video.atualizacaoId = atualizacaoId
                    })
                    videosEncontrados.push(...videos)
                  }
                }
              }
            }
          }
          
          this.videosDisponiveis = videosEncontrados
        } else {
          this.videosDisponiveis = []
        }
      } catch (error) {
        console.error('Erro ao buscar vídeos:', error)
        this.videosDisponiveis = []
      }
    },
    
    // Abrir diálogo de vídeo
    async abrirVideoDialog() {
      if (this.videosDisponiveis.length === 0) {
        this.$emit('show-snackbar', 'Nenhum vídeo disponível para esta atualização', 'info')
        return
      }
      
      this.videoDialog = true
      
      // Se houver apenas um vídeo, reproduzir automaticamente
      if (this.videosDisponiveis.length === 1) {
        await this.viewVideo(this.videosDisponiveis[0])
      } else if (this.videosDisponiveis.length > 1) {
        // Se houver múltiplos vídeos, reproduzir o primeiro
        await this.viewVideo(this.videosDisponiveis[0])
      }
    },
    
    // Visualizar vídeo
    async viewVideo(video) {
      // Verificar se o usuário está autenticado
      if (!authService.isAuthenticated()) {
        this.$emit('show-snackbar', 'Você não está autenticado. Faça login novamente.', 'error')
        return
      }
      
      try {
        const hostId = this.selectedHost?.hostId || this.selectedHost?.id
        if (!hostId) {
          throw new Error('Host ID não disponível')
        }
        
        const token = localStorage.getItem('authToken')
        
        if (!token) {
          this.$emit('show-snackbar', 'Token de autenticação não encontrado. Faça login novamente.', 'error')
          return
        }
        
        const encodedSistema = encodeURIComponent(video.sistema)
        const encodedData = encodeURIComponent(video.data)
        const encodedFileName = encodeURIComponent(video.nome)
        
        const fullUrl = getApiUrl(`/hosts/${hostId}/logs/${encodedSistema}/${encodedData}/${encodedFileName}`)
        
        const requestConfig = {
          headers: {
            ...getDefaultHeaders(),
            'Authorization': `Bearer ${token}`,
            'Accept': 'video/mp4'
          },
          responseType: 'blob',
          timeout: 30000
        }
        
        const response = await axios.get(fullUrl, requestConfig)
        
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'video/mp4' })
          const url = URL.createObjectURL(blob)
          
          this.selectedVideo = {
            nome: video.nome,
            url: url,
            blob: blob
          }
        }
      } catch (error) {
        console.error('Erro ao visualizar vídeo:', error)
        this.$emit('show-snackbar', 'Erro ao carregar vídeo: ' + (error.message || 'Erro desconhecido'), 'error')
      }
    },

    async salvarCronogramas() {
      if (!this.selectedHost) return
      
      // Validação 1: Porta bloqueada (Cronograma.md - documentação interna)
      if (this.selectedHost.porta_bloqueio === true && this.selectedHost.porta_liberado !== true) {
        this.$emit('show-snackbar', 'Este host possui bloqueio de porta. Realize a liberação na aba "Configuração" para poder configurar o cronograma.', 'error')
        return
      }
      
      // Validação 2: Conflitos de horário (Cronograma.md - linha 89-92)
      this.validarHorariosCronograma()
      if (this.horariosConflitantes.length > 0) {
        this.$emit('show-snackbar', `Conflitos de horário detectados: ${this.horariosConflitantes.join(', ')}`, 'error')
        return
      }

      this.salvandoCronograma = true
      try {
        // Preparar dados de cronograma no formato esperado pelo backend
        const cronogramaData = {
          hostId: this.selectedHost.hostId,
          vcon: this.cronogramaForm.vcon.enabled,
          vpra: this.cronogramaForm.vpra.enabled,
          vsuc: this.cronogramaForm.vsuc.enabled,
          vsup: this.cronogramaForm.vsup.enabled,
          vsyn: this.cronogramaForm.vsyn.enabled,
          horarioVcon: this.cronogramaForm.vcon.time,
          horarioVpra: this.cronogramaForm.vpra.time,
          horarioVsuc: this.cronogramaForm.vsuc.time,
          horarioVsup: this.cronogramaForm.vsup.time,
          horarioVsyn: this.cronogramaForm.vsyn.time,
          logsVideo: this.logsVideo
        }

        const response = await hostsService.createOrUpdateCronograma(cronogramaData)
        
        // Verificar se a resposta inclui o campo success
        if (response && response.success === false) {
          throw new Error(response.message || 'Erro ao salvar cronograma')
        }
        
        this.$emit('show-snackbar', 'Cronogramas salvos com sucesso!', 'success')
        this.$emit('refresh-data')
      } catch (error) {
        console.error('Erro ao salvar cronogramas:', error)
        
        // Verificar se é erro de bloqueio de porta
        if (error.response && error.response.status === 403 && error.response.data.bloqueado) {
          this.$emit('show-snackbar', error.response.data.error || 'Este host possui bloqueio de porta, realize a liberação para poder configurar o cronograma.', 'error')
          return
        }
        
        this.$emit('show-snackbar', error.message || 'Erro ao salvar cronogramas', 'error')
      } finally {
        this.salvandoCronograma = false
      }
    },

    async adicionarAgendamento(sistema) {
      if (!this.selectedHost) return

      const key = sistema.toLowerCase()
      const time = this.agendamentoForm[key].time

      if (!time) {
        this.$emit('show-snackbar', 'Por favor, selecione um horário (HH:MM)', 'warning')
        return
      }

      // Validação 1: Verificar template do Zabbix (Manual_Cronograma.md - linha 27-35)
      try {
        const templateResponse = await hostsService.verificarTemplatePadronizacao(this.selectedHost.hostId)
        
        if (!templateResponse.hasTemplate) {
          // Construir mensagem de erro com templates vinculados
          let errorMsg = 'Este host não está vinculado ao template "Atualizacao Automatica - Padronizacao" (ID: 12647). O agendamento manual requer este template.'
          
          if (templateResponse.templates && templateResponse.templates.length > 0) {
            errorMsg += '\n\nTemplates vinculados atualmente:'
            templateResponse.templates.forEach(t => {
              errorMsg += `\n- ${t.templateid}: ${t.name}`
            })
          }
          
          this.$emit('show-snackbar', errorMsg, 'error')
          return
        }
      } catch (templateError) {
        console.error('Erro ao validar template:', templateError)
        const errorMessage = templateError.response?.data?.error || templateError.message || 'Erro ao validar template do Zabbix'
        this.$emit('show-snackbar', errorMessage, 'error')
        return
      }

      // Validação 2: Verificar se sistema já está atualizado (Manual_Cronograma.md - linha 119-127)
      const versaoAtual = this.getSystemVersion(sistema)
      const versaoMaisRecente = this.latestVersions[sistema]
      if (versaoAtual && versaoMaisRecente && versaoAtual === versaoMaisRecente) {
        this.$emit('show-snackbar', `O sistema ${sistema} já está atualizado na versão ${versaoAtual}. Não é possível agendar uma atualização.`, 'info')
        return
      }

      // Validação 3: Formato HH:MM
      const timeRegex = /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/
      if (!timeRegex.test(time)) {
        this.$emit('show-snackbar', 'Formato de horário inválido. Use HH:MM', 'error')
        return
      }

      // Validação 4: Horário mínimo (07:30) e máximo (23:50) (Manual_Cronograma.md - linha 57-61)
      const [hours, minutes] = time.split(':').map(Number)
      const timeInMinutes = hours * 60 + minutes
      const minTime = 7 * 60 + 30  // 07:30
      const maxTime = 23 * 60 + 50 // 23:50

      if (timeInMinutes < minTime || timeInMinutes > maxTime) {
        this.$emit('show-snackbar', 'Horário deve estar entre 07:30 e 23:50 (Manual_Cronograma.md)', 'warning')
        return
      }
      
      // Validação 5: Horário futuro
      const now = new Date()
      const [currentHours, currentMinutes] = [now.getHours(), now.getMinutes()]
      const currentTimeInMinutes = currentHours * 60 + currentMinutes
      if (timeInMinutes <= currentTimeInMinutes) {
        this.$emit('show-snackbar', 'Horário deve ser futuro (posterior ao horário atual)', 'warning')
        return
      }
      
      // Validação 6: Conflito de 10 minutos com outros agendamentos (Manual_Cronograma.md - linha 61-67)
      const conflito = this.verificarConflito10Minutos(sistema, time)
      if (conflito) {
        this.$emit('show-snackbar', conflito, 'error')
        return
      }

      this.loadingAgendamento = true
      this.sistemaAgendando = sistema

      try {
        const response = await hostsService.createManualCronogramado(
          this.selectedHost.hostId,
          sistema,
          time
        )
        
        if (response && response.success) {
          this.$emit('show-snackbar', `Agendamento para ${sistema} criado com sucesso para hoje às ${time}!`, 'success')
          
          // Limpar campo
          this.agendamentoForm[key].time = ''
          
          // Recarregar agendamentos
          await this.loadAgendamentos()
        } else {
          throw new Error(response.message || 'Erro ao criar agendamento')
        }
      } catch (error) {
        console.error('Erro ao criar agendamento:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Erro ao criar agendamento'
        this.$emit('show-snackbar', errorMessage, 'error')
      } finally {
        this.loadingAgendamento = false
        this.sistemaAgendando = null
      }
    },

    async removerAgendamento(sistema) {
      if (!this.selectedHost) return

      this.loadingAgendamento = true
      this.sistemaAgendando = sistema

      try {
        // Deletar agendamento específico do sistema
        // Backend retorna 204 (No Content) quando bem-sucedido, então response será vazio
        await hostsService.deleteManualCronogramado(
          this.selectedHost.hostId,
          sistema
        )
        
        this.$emit('show-snackbar', `Agendamento de ${sistema} removido com sucesso!`, 'success')
        
        // Limpar o campo de input deste sistema
        const key = sistema.toLowerCase()
        this.agendamentoForm[key].time = ''
        
        // Recarregar agendamentos
        await this.loadAgendamentos()
        
      } catch (error) {
        console.error('Erro ao remover agendamento:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Erro ao remover agendamento'
        this.$emit('show-snackbar', errorMessage, 'error')
      } finally {
        this.loadingAgendamento = false
        this.sistemaAgendando = null
      }
    },

    async salvarPortaConfig() {
      if (!this.selectedHost) return

      this.salvandoPortaConfig = true
      try {
        const response = await hostsService.updatePortaConfig(
          this.selectedHost.hostId,
          this.portaConfigForm.porta_bloqueio,
          this.portaConfigForm.porta_liberado
        )
        
        if (response && response.success) {
          this.$emit('show-snackbar', 'Configurações de porta salvas com sucesso!', 'success')
          this.$emit('refresh-data')
          // Atualizar o selectedHost localmente
          if (this.selectedHost) {
            this.selectedHost.porta_bloqueio = this.portaConfigForm.porta_bloqueio
            this.selectedHost.porta_liberado = this.portaConfigForm.porta_liberado
          }
        } else {
          throw new Error(response.message || 'Erro ao salvar configurações')
        }
      } catch (error) {
        console.error('Erro ao salvar configurações:', error)
        this.$emit('show-snackbar', error.message || 'Erro ao salvar configurações', 'error')
      } finally {
        this.salvandoPortaConfig = false
      }
    },

    async handleButtonClick(sistema) {
      // Validação 1: Sistema já atualizado
      if (this.isAtualizado(sistema)) {
        const version = this.getSystemVersion(sistema)
        this.$emit('show-snackbar', `Sistema ${sistema} já está atualizado na versão ${version}`, 'info')
        return
      }
      
      // Validação 2: Base de dados muito grande (Manual.md - linha 38-42)
      if (this.isDatabaseSizeBlocked(sistema)) {
        this.$emit('show-snackbar', `Atualização manual bloqueada: Base de dados ${sistema} excede 50GB`, 'error')
        return
      }
      
      // Validação 3: Verificar score de segurança (Manual.md - linha 111-120)
      const versaoAtual = this.getSystemVersion(sistema)
      if (versaoAtual) {
        try {
          const sistemasParaValidar = [{ sistema: sistema, versao: versaoAtual }]
          const response = await hostsService.calcularScoreSeguranca(sistemasParaValidar)
          
          if (response && response.success && response.scores) {
            const score = response.scores.find(s => s.sistema === sistema)
            if (score && score.bloqueado) {
              let mensagem = ''
              if (score.versao_nao_encontrada) {
                mensagem = `Atualização bloqueada: ${sistema} possui versão muito antiga (${score.versao_cliente}) que não existe no histórico. Recomenda-se atualização manual supervisionada.`
              } else {
                const versoesAtrasadas = score.versoes_atrasadas || 0
                mensagem = `Atualização bloqueada: ${sistema} está muito desatualizado (Score=${score.peso_total.toFixed(1)}, ${score.diferenca_dias} dias, ${versoesAtrasadas} versões atrasadas). Recomenda-se atualização manual supervisionada.`
              }
              this.$emit('show-snackbar', mensagem, 'error')
              return
            }
          }
        } catch (scoreError) {
          console.error('Erro ao validar score de segurança:', scoreError)
          // Não bloquear se houver erro no cálculo do score, apenas logar
        }
      }
      
      // Validação 4: Verificar se já está atualizando (Manual.md - linha 44-47)
      if (this.atualizando) {
        this.$emit('show-snackbar', `Aguarde! Uma atualização de ${this.atualizando} já está em andamento neste host.`, 'warning')
        return
      }
      
      // Se chegou até aqui, pode atualizar
      this.$emit('atualizar-sistema', sistema)
    },

    closeDialog() {
      this.internalDialog = false
    },

    // Métodos auxiliares
    hasSystem(sistema) {
      if (!this.selectedHost || !sistema) return false
      const key = `has${sistema}`
      return Boolean(this.selectedHost[key])
    },

    getSystemVersion(sistema) {
      if (!this.selectedHost || !sistema) return 'N/A'
      const key = `${sistema.toLowerCase()}Version`
      return this.selectedHost[key] || 'N/A'
    },

    isAtualizado(sistema) {
      const currentVersion = this.getSystemVersion(sistema)
      const latestVersion = this.latestVersions[sistema]
      return currentVersion && latestVersion && currentVersion === latestVersion
    },

    isDatabaseSizeBlocked(sistema) {
      if (!this.selectedHost || !sistema) return false
      const sizeKey = `${sistema.toLowerCase()}Size`
      const size = this.selectedHost[sizeKey]
      
      // Verificar se o tamanho está acima de 50GB (50 * 1024 * 1024 * 1024 bytes)
      const limitSize = 50 * 1024 * 1024 * 1024
      return size && size > limitSize
    },

    getButtonColor(sistema) {
      if (this.atualizando === sistema) {
        return 'orange'
      }
      if (this.isAtualizado(sistema)) {
        return 'success'
      }
      if (this.isDatabaseSizeBlocked(sistema)) {
        return 'grey'
      }
      if (sistema === 'VSUP' && !this.podeAtualizarVSUP) {
        return 'grey'
      }
      return 'error'
    },

    isButtonDisabled(sistema) {
      if (this.atualizando !== null) {
        return true
      }
      if (this.isDatabaseSizeBlocked(sistema)) {
        return true
      }
      // VSUP: bloquear se VSUC não estiver em 8.04a ou superior
      if (sistema === 'VSUP' && !this.podeAtualizarVSUP) {
        return true
      }
      return false
    },

    getButtonIcon(sistema) {
      if (this.atualizando === sistema) {
        return 'mdi-loading'
      }
      if (this.isAtualizado(sistema)) {
        return 'mdi-check'
      }
      if (this.isDatabaseSizeBlocked(sistema)) {
        return 'mdi-lock'
      }
      if (sistema === 'VSUP' && !this.podeAtualizarVSUP) {
        return 'mdi-lock'
      }
      return 'mdi-rocket-launch'
    },

    getButtonText(sistema) {
      if (this.atualizando === sistema) {
        return 'Atualizando...'
      }
      if (this.isAtualizado(sistema)) {
        return 'Atualizado'
      }
      if (this.isDatabaseSizeBlocked(sistema)) {
        return 'Bloqueado'
      }
      if (sistema === 'VSUP' && !this.podeAtualizarVSUP) {
        return 'Bloqueado (VSUC < 8.04a)'
      }
      return 'Atualizar Agora'
    },

    getIconeSistema(sistema) {
      switch (sistema) {
        case 'VPRA':
          return '/ico_vpra.png'
        case 'VSUC':
          return '/ico_vsuc.png'
        case 'VSUP':
          return '/ico_vsup.png'
        case 'VCON':
          return '/ico_vcon.png'
        case 'VSYN':
          return '/ico_vsyn.png'
        default:
          return '/ico_vpra.png'
      }
    },

    formatarDataHora(datetime) {
      if (!datetime) return ''
      try {
        return new Date(datetime).toLocaleString('pt-BR')
      } catch {
        return datetime
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      try {
        return new Date(dateString).toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch {
        return dateString
      }
    },

    formatBaseSize(sizeValue) {
      if (!sizeValue || sizeValue === 0 || sizeValue === '0') return '0 GB'
      
      // Se já vier como string (exemplo: "1.8"), é porque já está em GB
      if (typeof sizeValue === 'string') {
        const gb = parseFloat(sizeValue)
        return `${gb.toFixed(2)} GB`
      }
      
      // Se vier como número grande (bytes), converter para GB
      if (sizeValue > 100) {
        const gb = sizeValue / (1024 * 1024 * 1024)
        return `${gb.toFixed(2)} GB`
      }
      
      // Se for número pequeno (já em GB)
      return `${parseFloat(sizeValue).toFixed(2)} GB`
    },

    formatDuracao(segundos) {
      if (!segundos || segundos <= 0) return '0s'
      
      const segundosInteiros = Math.round(Number(segundos))
      const horas = Math.floor(segundosInteiros / 3600)
      const minutos = Math.floor((segundosInteiros % 3600) / 60)
      const segs = segundosInteiros % 60
      
      let resultado = ''
      if (horas > 0) resultado += `${horas}h `
      if (minutos > 0) resultado += `${minutos}m `
      if (segs > 0 || (horas === 0 && minutos === 0)) resultado += `${segs}s`
      
      return resultado.trim()
    },

    formatLogContent(content) {
      if (!content) return ''
      return formatarLog(content, false)
    },
    
    // Verificar se sistema tem agendamento manual
    temAgendamento(sistema) {
      const key = sistema.toLowerCase()
      return this.agendamentosExistentes[key] && this.agendamentosExistentes[key].length > 0
    },
    
    // Obter horário do agendamento manual
    getAgendamentoHorario(sistema) {
      const key = sistema.toLowerCase()
      if (this.agendamentosExistentes[key] && this.agendamentosExistentes[key].length > 0) {
        return this.agendamentosExistentes[key][0].horario
      }
      return ''
    },
    
    // Métodos de cor do Dashboard (mesmas cores dos chips e gráficos)
    getCorMetodo(metodo) {
      // Cores diferentes para melhor visibilidade em ambos os temas
      // Para CRONOGRAMA_RETRY, retornar undefined para usar style customizado
      if (metodo === 'CRONOGRAMA_RETRY') {
        return undefined // Será tratado com style customizado
      }
      const cores = {
        'MANUAL': 'primary', // Azul
        'MANUAL_CRONOGRAMA': 'grey', // Cinza
        'CRONOGRAMA': this.$vuetify.theme.dark ? 'deep-purple' : 'secondary', // Roxo
        'AUTOMÁTICO': 'info'
      }
      return cores[metodo] || 'grey'
    },
    
    // Método auxiliar para obter estilo customizado para chips com cores hex
    getChipStyle(metodo) {
      if (metodo === 'CRONOGRAMA_RETRY') {
        // Para chips outlined, definir a cor da borda e do texto (cor fixa #03dac6 - ciano/turquesa)
        return {
          borderColor: '#03dac6',
          color: '#03dac6'
        }
      }
      return {}
    },
    
    // Validação de horários do cronograma (mínimo 10 minutos entre sistemas)
    validarHorariosCronograma() {
      this.horariosConflitantes = []
      
      const horariosAtivos = []
      this.sistemasDisponiveis.forEach(sistema => {
        const key = sistema.toLowerCase()
        if (this.cronogramaForm[key].enabled && this.cronogramaForm[key].time) {
          horariosAtivos.push({
            sistema: sistema,
            horario: this.cronogramaForm[key].time
          })
        }
      })
      
      // Verificar conflitos entre pares de horários
      for (let i = 0; i < horariosAtivos.length; i++) {
        for (let j = i + 1; j < horariosAtivos.length; j++) {
          const h1 = horariosAtivos[i]
          const h2 = horariosAtivos[j]
          
          const [h1Hours, h1Minutes] = h1.horario.split(':').map(Number)
          const [h2Hours, h2Minutes] = h2.horario.split(':').map(Number)
          
          const h1TotalMinutes = h1Hours * 60 + h1Minutes
          const h2TotalMinutes = h2Hours * 60 + h2Minutes
          
          const diferenca = Math.abs(h1TotalMinutes - h2TotalMinutes)
          
          if (diferenca < 10) {
            this.horariosConflitantes.push(`${h1.sistema} (${h1.horario}) e ${h2.sistema} (${h2.horario}) têm menos de 10 minutos de diferença`)
          }
        }
      }
    },
    
    // Verificar conflito de 10 minutos para agendamento manual
    verificarConflito10Minutos(sistemaAtual, horarioAtual) {
      const [currentHours, currentMinutes] = horarioAtual.split(':').map(Number)
      const currentTimeInMinutes = currentHours * 60 + currentMinutes
      
      // Verificar com cronogramas ativos
      for (const sistema of this.sistemasDisponiveis) {
        const key = sistema.toLowerCase()
        if (this.cronogramaForm[key].enabled && this.cronogramaForm[key].time) {
          const [cronHours, cronMinutes] = this.cronogramaForm[key].time.split(':').map(Number)
          const cronTimeInMinutes = cronHours * 60 + cronMinutes
          const diferenca = Math.abs(currentTimeInMinutes - cronTimeInMinutes)
          
          if (diferenca < 10) {
            return `Conflito: ${sistemaAtual} (${horarioAtual}) está a menos de 10 minutos do cronograma de ${sistema} (${this.cronogramaForm[key].time})`
          }
        }
      }
      
      // Verificar com outros agendamentos manuais existentes
      for (const sistema of this.sistemasDisponiveis) {
        if (sistema === sistemaAtual) continue
        
        const key = sistema.toLowerCase()
        const agendamentos = this.agendamentosExistentes[key] || []
        
        for (const agendamento of agendamentos) {
          const horarioAgendado = agendamento.horario
          const [agendHours, agendMinutes] = horarioAgendado.split(':').map(Number)
          const agendTimeInMinutes = agendHours * 60 + agendMinutes
          const diferenca = Math.abs(currentTimeInMinutes - agendTimeInMinutes)
          
          if (diferenca < 10) {
            return `Conflito: ${sistemaAtual} (${horarioAtual}) está a menos de 10 minutos do agendamento de ${sistema} (${horarioAgendado})`
          }
        }
      }
      
      return null
    },
    
    // Métodos relacionados ao Score de Segurança
    async buscarScoresSeguranca(host) {
      if (!host) return

      const sistemasParaCalcular = []
      
      if (host.hasVCON && host.vconVersion) {
        sistemasParaCalcular.push({ sistema: 'VCON', versao: host.vconVersion })
      }
      if (host.hasVPRA && host.vpraVersion) {
        sistemasParaCalcular.push({ sistema: 'VPRA', versao: host.vpraVersion })
      }
      if (host.hasVSUC && host.vsucVersion) {
        sistemasParaCalcular.push({ sistema: 'VSUC', versao: host.vsucVersion })
      }
      if (host.hasVSUP && host.vsupVersion) {
        sistemasParaCalcular.push({ sistema: 'VSUP', versao: host.vsupVersion })
      }
      if (host.hasVSYN && host.vsynVersion) {
        sistemasParaCalcular.push({ sistema: 'VSYN', versao: host.vsynVersion })
      }

      if (sistemasParaCalcular.length === 0) {
        this.scoresSistemas = {}
        return
      }

      this.carregandoScores = true
      try {
        const response = await hostsService.calcularScoreSeguranca(sistemasParaCalcular)
        
        if (response && response.success && response.scores) {
          const scoresMap = {}
          response.scores.forEach(score => {
            scoresMap[score.sistema] = score
          })
          this.scoresSistemas = scoresMap
        } else {
          this.scoresSistemas = {}
        }
      } catch (error) {
        console.error('Erro ao buscar scores de segurança:', error)
        this.scoresSistemas = {}
      } finally {
        this.carregandoScores = false
      }
    },

    async carregarPodeAtualizarVSUP() {
      if (!this.selectedHost || !this.selectedHost.hasVSUP || !this.selectedHost.hasVSUC) {
        this.podeAtualizarVSUP = true
        this.mensagemBloqueioVSUP = ''
        return
      }
      try {
        const res = await hostsService.podeAtualizarVSUP(this.selectedHost.hostId)
        if (res && res.success) {
          this.podeAtualizarVSUP = res.permitido
          this.mensagemBloqueioVSUP = res.mensagem || ''
        } else {
          this.podeAtualizarVSUP = true
          this.mensagemBloqueioVSUP = ''
        }
      } catch (e) {
        console.error('Erro ao verificar se pode atualizar VSUP:', e)
        this.podeAtualizarVSUP = true
        this.mensagemBloqueioVSUP = ''
      }
    },
    
    async carregarConfiguracoesScore() {
      try {
        const response = await configuracaoScoreService.buscarComoObjeto()
        if (response && response.success) {
          const loadedConfig = {}
          for (const key in response.configs) {
            loadedConfig[key] = parseFloat(response.configs[key])
          }
          this.configuracoesScore = { ...loadedConfig }
        }
      } catch (error) {
        console.error('Erro ao carregar configurações de score:', error)
      }
    },
    
    // Verifica se deve exibir o indicador de score
    // Retorna false se sistema está atualizado (score 0) - não precisa mostrar nada
    deveMostrarScore(score) {
      if (!score) return false
      
      // Não mostrar se sistema está atualizado (score 0)
      const scoreNumero = Math.round(score.peso_total)
      if (scoreNumero === 0 && !score.versao_nao_encontrada && !score.bloqueado) {
        return false
      }
      
      // Mostrar em todos os outros casos (bloqueado, versão não encontrada, ou score > 0)
      return true
    },
    
    deveMostrarIcone(score) {
      if (!score) return false
      
      // Mostrar ícone apenas para casos extremos de desatualização:
      // - Versão não encontrada ou bloqueado (muito desatualizado) → ⛔
      // Não mostrar ✅ para atualizado, pois não exibimos o score nesse caso
      return score.versao_nao_encontrada || score.bloqueado
    },
    
    getScoreIcon(score) {
      if (!score) return ''
      
      // ⛔ para muito desatualizado (versão não encontrada ou bloqueado)
      if (score.versao_nao_encontrada || score.bloqueado) {
        return '⛔'
      }
      
      // Não retornar ✅ pois não exibimos score quando está atualizado
      return ''
    },
    
    // Calcula a cor do círculo baseada no score
    // 0.1 a 15: verde → amarelo
    // >15 a 100+: laranja → vermelho
    getScoreColorStyle(score) {
      if (!score) {
        return { backgroundColor: '#9e9e9e' } // Cinza padrão
      }
      
      const pesoTotal = score.peso_total || 0
      
      // Escala verde → amarelo (0.1 a 15)
      if (pesoTotal >= 0.1 && pesoTotal <= 15) {
        // Normalizar de 0.1-15 para 0-1
        const normalized = (pesoTotal - 0.1) / (15 - 0.1)
        
        // Verde (#4caf50) → Amarelo (#ffc107)
        const r1 = 76, g1 = 175, b1 = 80  // Verde
        const r2 = 255, g2 = 193, b2 = 7  // Amarelo
        
        const r = Math.round(r1 + (r2 - r1) * normalized)
        const g = Math.round(g1 + (g2 - g1) * normalized)
        const b = Math.round(b1 + (b2 - b1) * normalized)
        
        return { backgroundColor: `rgb(${r}, ${g}, ${b})` }
      }
      
      // Escala laranja → vermelho (>15 a 100+)
      if (pesoTotal > 15) {
        // Normalizar de 15-100 para 0-1 (máximo 100)
        const maxScore = 100
        const normalized = Math.min((pesoTotal - 15) / (maxScore - 15), 1.0)
        
        // Laranja (#ff9800) → Vermelho (#f44336)
        const r1 = 255, g1 = 152, b1 = 0   // Laranja
        const r2 = 244, g2 = 67, b2 = 54   // Vermelho
        
        const r = Math.round(r1 + (r2 - r1) * normalized)
        const g = Math.round(g1 + (g2 - g1) * normalized)
        const b = Math.round(b1 + (b2 - b1) * normalized)
        
        return { backgroundColor: `rgb(${r}, ${g}, ${b})` }
      }
      
      // Para scores < 0.1, usar verde
      return { backgroundColor: '#4caf50' }
    },
    
    getScoreTooltip(score, sistema) {
      if (!score) return ''
      
      if (score.versao_nao_encontrada) {
        return `Agendamento bloqueado: O sistema ${sistema} possui versão muito antiga (${score.versao_cliente}) que não existe no histórico. Recomenda-se atualização manual.`
      }
      
      if (score.bloqueado) {
        const versoesAtrasadas = score.versoes_atrasadas || 0
        return `Agendamento bloqueado: O sistema ${sistema} está muito desatualizado (peso_total=${score.peso_total.toFixed(1)}, dias=${score.diferenca_dias}, versões atrasadas=${versoesAtrasadas}). Recomenda-se atualização manual.`
      }
      
      const limiteAceito = this.configuracoesScore.SCORE_LIMITE_ACEITO || 0
      const versoesAtrasadas = score.versoes_atrasadas || 0
      const pesoTotal = score.peso_total.toFixed(1)
      
      if (score.peso_total >= limiteAceito) {
        return `Score (peso_total): ${pesoTotal} (${score.diferenca_dias} dias atrasado, ${versoesAtrasadas} versões). Atenção.`
      }
      
      return `Score (peso_total): ${pesoTotal} (${score.diferenca_dias} dias atrasado, ${versoesAtrasadas} versões). Sistema seguro para atualizar.`
    },
    
    // Toggle ativar/desativar todos os cronogramas
    toggleAtivarTudoCronogramas() {
      if (!this.selectedHost) return
      
      const novoEstado = !this.todosCronogramasAtivados
      
      // Ativar/desativar apenas os sistemas disponíveis para este host
      if (this.selectedHost.hasVCON) {
        this.cronogramaForm.vcon.enabled = novoEstado
      }
      if (this.selectedHost.hasVPRA) {
        this.cronogramaForm.vpra.enabled = novoEstado
      }
      if (this.selectedHost.hasVSUC) {
        this.cronogramaForm.vsuc.enabled = novoEstado
      }
      if (this.selectedHost.hasVSUP) {
        this.cronogramaForm.vsup.enabled = novoEstado
      }
      if (this.selectedHost.hasVSYN) {
        this.cronogramaForm.vsyn.enabled = novoEstado
      }
      
      // Validar horários após a mudança
      this.$nextTick(() => {
        this.validarHorariosCronograma()
      })
    },
    
    // Validar horário mínimo
    validarHorarioMinimo() {
      this.erroHorarioMinimo = ''
      
      if (!this.horarioMinimo) {
        return // Campo vazio é válido (opcional)
      }
      
      const [hora, minuto] = this.horarioMinimo.split(':').map(Number)
      
      // Validar que termina em 0
      if (minuto % 10 !== 0) {
        this.erroHorarioMinimo = 'O horário deve terminar em 0 (ex: 02:00, 03:10)'
        return
      }
      
      // Validar que está entre 01:30 e 07:00
      const minutosTotais = hora * 60 + minuto
      const minPermitido = 1 * 60 + 30 // 01:30
      const maxPermitido = 7 * 60 + 0 // 07:00
      
      if (minutosTotais < minPermitido || minutosTotais > maxPermitido) {
        this.erroHorarioMinimo = 'O horário deve estar entre 01:30 e 07:00'
        return
      }
    },
    
    // Aplicar sugestão de horários
    async aplicarSugestaoHorarios() {
      if (!this.temSistemasCronogramaSelecionados) {
        this.$emit('show-snackbar', 'Selecione pelo menos um sistema para aplicar a sugestão de horários', 'warning')
        return
      }

      // Validar horário mínimo antes de prosseguir
      this.validarHorarioMinimo()
      if (this.erroHorarioMinimo) {
        return
      }

      this.carregandoSugestao = true
      try {
        // Identificar sistemas selecionados na ordem: VCON, VPRA, VSUC, VSUP, VSYN
        const sistemasSelecionados = []
        if (this.cronogramaForm.vcon.enabled) sistemasSelecionados.push('vcon')
        if (this.cronogramaForm.vpra.enabled) sistemasSelecionados.push('vpra')
        if (this.cronogramaForm.vsuc.enabled) sistemasSelecionados.push('vsuc')
        if (this.cronogramaForm.vsup.enabled) sistemasSelecionados.push('vsup')
        if (this.cronogramaForm.vsyn.enabled) sistemasSelecionados.push('vsyn')

        const quantidadeSistemas = sistemasSelecionados.length

        // Buscar sugestão de horários (backend já retorna com gap de 10 minutos)
        const response = await hostsService.getSugestaoHorarios(
          this.horarioMinimo || null,
          quantidadeSistemas
        )
        
        if (!response.success || !response.horarios || response.horarios.length === 0) {
          this.$emit('show-snackbar', 'Não foi possível obter sugestões de horários', 'error')
          return
        }

        const horariosSugeridos = response.horarios

        // Mapear sistemas para seus campos de horário
        const sistemaParaCampo = {
          'vcon': 'vcon',
          'vpra': 'vpra',
          'vsuc': 'vsuc',
          'vsup': 'vsup',
          'vsyn': 'vsyn'
        }

        // Aplicar horários retornados pelo backend (já com gap de 10 minutos)
        for (let i = 0; i < sistemasSelecionados.length && i < horariosSugeridos.length; i++) {
          const sistema = sistemasSelecionados[i]
          const horario = horariosSugeridos[i]
          
          // Aplicar horário no formato correto (HH:MM)
          this.cronogramaForm[sistema].time = horario
        }

        // Validar horários após aplicar sugestões
        this.$nextTick(() => {
          this.validarHorariosCronograma()
        })

        this.$emit('show-snackbar', 'Horários sugeridos aplicados com sucesso!', 'success')
      } catch (error) {
        console.error('Erro ao aplicar sugestão de horários:', error)
        this.$emit('show-snackbar', 'Erro ao aplicar sugestão de horários: ' + (error.message || 'Erro desconhecido'), 'error')
      } finally {
        this.carregandoSugestao = false
      }
    }
  }
}
</script>

<style scoped>
/* Usar cores do tema do Vuetify - suporte para dark e light */
.unified-tabs {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.theme--light .unified-tabs {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.tab-button {
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

/* System Row Styles */
.system-row {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  margin-bottom: 8px;
  display: grid;
  grid-template-columns: 250px 1fr 1fr;
  gap: 15px;
  align-items: center;
  padding: 10px;
  transition: border-color 0.2s;
}

.theme--light .system-row {
  background-color: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.system-row:hover {
  border-color: rgba(255, 255, 255, 0.24);
}

.theme--light .system-row:hover {
  border-color: rgba(0, 0, 0, 0.24);
}

.col-info {
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  padding-right: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.theme--light .col-info {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.sys-header {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
}

.sys-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  overflow: hidden;
}

.theme--light .sys-icon {
  background: rgba(0, 0, 0, 0.06);
}

.sistema-icon-small {
  width: 30px;
  height: 30px;
  object-fit: contain;
  border-radius: 50%;
}

.sys-name {
  font-weight: bold;
  font-size: 1.1rem;
  color: inherit;
}

.sys-version {
  font-size: 0.8rem;
  color: #ff3d00;
  display: flex;
  align-items: center;
  gap: 5px;
}

.theme--light .sys-version {
  color: #d32f2f;
}

.sys-version.ok {
  color: #00e676;
}

.theme--light .sys-version.ok {
  color: #4caf50;
}

.btn-manual {
  margin-top: 8px;
}

.col-config {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  font-weight: bold;
}

.theme--light .section-label {
  color: rgba(0, 0, 0, 0.6);
}

.input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.time-input {
  flex: 1;
  max-width: 180px;
}

.status-text {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}

.theme--light .status-text {
  color: rgba(0, 0, 0, 0.7);
}

/* Agendamento existente */
.agendamento-existente {
  display: flex;
  align-items: center;
  min-height: 40px;
}

.agendamento-chip {
  font-weight: 600;
  font-size: 0.875rem;
}

.agendamento-bloqueado {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-height: 40px;
  justify-content: center;
}

.bloqueio-chip {
  font-weight: 600;
  font-size: 0.75rem;
}

.bloqueio-texto {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  margin-left: 2px;
}

.theme--light .bloqueio-texto {
  color: rgba(0, 0, 0, 0.6);
}

/* Config Grid */
.config-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.config-info h4 {
  margin-bottom: 5px;
  font-size: 1.1rem;
  color: inherit;
}

.config-info p {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
}

.theme--light .config-info p {
  color: rgba(0, 0, 0, 0.6);
}

/* Log Table - Vuetify gerencia automaticamente */

.sistema-logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
  padding: 8px;
  overflow: hidden;
}

.theme--light .sistema-logo-container {
  background: rgba(0, 0, 0, 0.06);
}

.sistema-logo-rounded {
  width: 30px;
  height: 30px;
  object-fit: contain;
  border-radius: 50%;
}

/* Log Modal - Vuetify gerencia automaticamente via v-card */

.log-header-bar {
  background-color: rgba(255, 255, 255, 0.05);
  padding: 15px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.theme--light .log-header-bar {
  background-color: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.log-title-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.log-type-title {
  font-size: 1rem;
  font-weight: 700;
  color: inherit;
  display: flex;
  align-items: center;
  text-transform: uppercase;
}

.log-sub-info {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
}

.theme--light .log-sub-info {
  color: rgba(0, 0, 0, 0.6);
}

.terminal-body {
  background-color: rgba(0, 0, 0, 0.5);
  color: rgba(255, 255, 255, 0.87);
  padding: 20px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
}

.theme--light .terminal-body {
  background-color: rgba(0, 0, 0, 0.02);
  color: rgba(0, 0, 0, 0.87);
}

.log-content {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.87);
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
}

.theme--light .log-content {
  color: rgba(0, 0, 0, 0.87);
}

/* Estilos para highlights do formatterlog */
.log-content >>> .timestamp-highlight {
  color: #00e676;
  font-weight: bold;
}

.theme--light .log-content >>> .timestamp-highlight {
  color: #4caf50;
}

.log-content >>> .error-highlight {
  color: #ff5252;
  font-weight: bold;
}

.theme--light .log-content >>> .error-highlight {
  color: #f44336;
}

.log-content >>> .debug-highlight {
  color: #64b5f6;
  font-weight: bold;
}

.theme--light .log-content >>> .debug-highlight {
  color: #2196f3;
}

.log-footer-bar {
  padding: 15px 20px;
  background-color: rgba(255, 255, 255, 0.05);
}

.theme--light .log-footer-bar {
  background-color: rgba(0, 0, 0, 0.02);
}

/* Indicador circular de score no canto superior direito */
.score-indicator {
  position: absolute;
  top: 1px;
  right: 1px;
  z-index: 10;
  width: 20px;
  height: 20px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.score-indicator:hover {
  transform: scale(1.15);
}

/* Círculo colorido sólido (sem número) */
.score-color-circle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.score-indicator:hover .score-color-circle {
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
}

/* Indicador apenas com ícone (sem círculo) */
.score-icon-only {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.score-icon {
  font-size: 16px;
  line-height: 1;
  display: block;
}

</style>

